/**
 * Compass Draw Schedule Generator
 *
 * Compass draw is a cascading consolation format built from up to 8 named
 * single-elimination mini-brackets ("directions"), each fed by the real
 * losers of one specific round of another bracket:
 *
 *   E (East)  — main draw, standard single-elimination on all entrants.
 *   W (West)  — Round 1 losers of E.
 *   N (North) — Round 2 losers of E.
 *   S (South) — Round 3 losers of E.
 *   NW        — Round 1 losers of W.
 *   SW        — Round 2 losers of W (only if W itself is big enough).
 *   NE        — Round 1 losers of N.
 *   SE        — Round 1 losers of S.
 *
 * Which of the 7 consolation brackets actually populate depends entirely on
 * entrant count — e.g. E needs 3 rounds before S can exist at all, and S
 * needs 2+ real Round-3 losers before SE can exist on top of that. A bracket
 * with fewer than 2 incoming entrants is skipped rather than created empty.
 *
 * Only E's Round 1 has real participants at generation time (with byes filled
 * in exactly like generateSingleElimination). Every other round in every
 * bracket — including E's own Round 2+ — is a teams-TBD placeholder: within a
 * bracket, winners resolve via previousMatchA/previousMatchB the same way the
 * other generators do it; across brackets, losers resolve via the new
 * round-level group_name/next_round_id/loser_next_round_id fields (mirroring
 * the backend Prisma Rounds model), which a later save-wiring step resolves
 * to real round ids once rounds are persisted. Actually assigning *which*
 * specific loser lands in *which* match slot (and which slot absorbs a
 * consolation-bracket bye) is that save-wiring step's job, not this one —
 * this generator only needs the counts and the cross-bracket links right.
 */

const generateMatchId = () => `compass-match-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const generateRoundId = () => `compass-round-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const nextPowerOf2 = (n) => {
  let power = 1;
  while (power < n) power *= 2;
  return power;
};

export const COMPASS_GROUP_LABELS = {
  E: 'East',
  W: 'West',
  N: 'North',
  S: 'South',
  NW: 'Northwest',
  SW: 'Southwest',
  NE: 'Northeast',
  SE: 'Southeast',
};

export const COMPASS_GROUPS = ['E', 'W', 'N', 'S', 'NW', 'SW', 'NE', 'SE'];

/**
 * Which group feeds each consolation bracket, and from which literal round
 * number of the feeder. `E` isn't listed — it's the root, fed by real entrants
 * rather than another bracket's losers.
 */
export const COMPASS_FEEDER_MAP = {
  W: { from: 'E', round: 1 },
  N: { from: 'E', round: 2 },
  S: { from: 'E', round: 3 },
  NW: { from: 'W', round: 1 },
  SW: { from: 'W', round: 2 },
  NE: { from: 'N', round: 1 },
  SE: { from: 'S', round: 1 },
};

const getRoundSuffix = (roundNumber, totalRounds) => {
  const roundsFromFinal = totalRounds - roundNumber;
  switch (roundsFromFinal) {
    case 0: return 'Final';
    case 1: return 'Semi-Finals';
    case 2: return 'Quarter-Finals';
    case 3: return 'Round of 16';
    case 4: return 'Round of 32';
    default: return `Round ${roundNumber}`;
  }
};

const getCompassRoundName = (groupName, roundNumber, totalRounds) =>
  `${COMPASS_GROUP_LABELS[groupName]} ${getRoundSuffix(roundNumber, totalRounds)}`;

/**
 * How many *real* losers a given round of a bracket produces. Only round 1
 * can contain byes (every later round is real-winner vs real-winner by
 * construction), so round 1's real-loser count is its match count minus the
 * bracket's byes; every later round's real-loser count is just its match
 * count.
 */
const realLosersInRound = (group, roundNumber) => {
  if (!group || roundNumber > group.totalRounds) return 0;
  const matchesInRound = group.bracketSize / Math.pow(2, roundNumber);
  return roundNumber === 1 ? matchesInRound - group.byesNeeded : matchesInRound;
};

/**
 * Builds one bracket's single-elimination skeleton. With `realTeams` given
 * (E only), Round 1 is seeded with actual participants + BYE placeholders,
 * same as generateSingleElimination. Without it (every consolation bracket),
 * Round 1 is `bracketSize / 2` teams-TBD slots — the real teams are whichever
 * losers eventually drop out of the feeder bracket, not known yet.
 *
 * Returns null (bracket doesn't populate) when fewer than 2 entrants would
 * feed it — a single incoming loser has no one to play.
 */
const buildBracket = (groupName, entrantCount, realTeams, matchNumberRef, allMatches, allRounds) => {
  if (entrantCount < 2) return null;

  const bracketSize = nextPowerOf2(entrantCount);
  const byesNeeded = bracketSize - entrantCount;
  const totalRounds = Math.log2(bracketSize);
  const rounds = [];

  const round1Matches = [];
  if (realTeams) {
    const teamsWithByes = [...realTeams];
    for (let i = 0; i < byesNeeded; i++) {
      teamsWithByes.splice(i * 2 + 1, 0, { id: `bye-${groupName}-${i}`, name: 'BYE', isBye: true });
    }
    for (let i = 0; i < teamsWithByes.length; i += 2) {
      const teamA = teamsWithByes[i];
      const teamB = teamsWithByes[i + 1];
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumberRef.value++,
        roundNumber: 1,
        roundName: getCompassRoundName(groupName, 1, totalRounds),
        groupName,
        teamA,
        teamB,
        isBye: teamA.isBye || teamB.isBye,
        winner: teamA.isBye ? teamB : (teamB.isBye ? teamA : null),
        status: teamA.isBye || teamB.isBye ? 'completed' : 'pending',
        scheduledAt: null,
        venue: null,
      };
      round1Matches.push(match);
      allMatches.push(match);
    }
  } else {
    for (let i = 0; i < bracketSize / 2; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumberRef.value++,
        roundNumber: 1,
        roundName: getCompassRoundName(groupName, 1, totalRounds),
        groupName,
        teamA: null,
        teamB: null,
        isBye: false,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      round1Matches.push(match);
      allMatches.push(match);
    }
  }

  const round1 = {
    id: generateRoundId(),
    roundNumber: 1,
    roundName: getCompassRoundName(groupName, 1, totalRounds),
    roundType: 'elimination',
    group_name: groupName,
    matches: round1Matches,
    next_round_id: null,
    loser_next_round_id: null,
  };
  rounds.push(round1);

  let previousRound = round1;
  let previousMatches = round1Matches;
  for (let r = 2; r <= totalRounds; r++) {
    const matchesInRound = previousMatches.length / 2;
    const roundMatches = [];
    for (let i = 0; i < matchesInRound; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumberRef.value++,
        roundNumber: r,
        roundName: getCompassRoundName(groupName, r, totalRounds),
        groupName,
        teamA: null,
        teamB: null,
        previousMatchA: previousMatches[i * 2]?.id,
        previousMatchB: previousMatches[i * 2 + 1]?.id,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      roundMatches.push(match);
      allMatches.push(match);
    }

    const round = {
      id: generateRoundId(),
      roundNumber: r,
      roundName: getCompassRoundName(groupName, r, totalRounds),
      roundType: 'elimination',
      group_name: groupName,
      matches: roundMatches,
      next_round_id: null,
      loser_next_round_id: null,
    };
    previousRound.next_round_id = round.id; // in-bracket winner path
    rounds.push(round);

    previousRound = round;
    previousMatches = roundMatches;
  }

  rounds.forEach(round => allRounds.push(round));

  return { groupName, entrantCount, bracketSize, byesNeeded, totalRounds, rounds };
};

/**
 * COMPASS DRAW GENERATOR
 *
 * Builds E from real entrants, then cascades W/N/S from E's Round 1/2/3
 * losers, then NW/SW/NE/SE from W/N/S in turn — wiring each feeder round's
 * `loser_next_round_id` to the fed bracket's Round 1 id as it goes.
 */
export const generateCompassDraw = (participants, options = {}) => {
  if (!participants || participants.length < 2) {
    throw new Error('At least 2 participants are required');
  }

  const { seeded = false, seedOrder = null } = options;
  const teams = seeded && seedOrder ? seedOrder : shuffleArray([...participants]);

  const allMatches = [];
  const allRounds = [];
  const matchNumberRef = { value: 1 };
  const groups = {};

  const build = (groupName, entrantCount, realTeams = null) => {
    const group = buildBracket(groupName, entrantCount, realTeams, matchNumberRef, allMatches, allRounds);
    if (group) groups[groupName] = group;
    return group;
  };

  const link = (feederGroupName, feederRoundNumber, fedGroup) => {
    if (!fedGroup) return;
    const feederRound = groups[feederGroupName]?.rounds[feederRoundNumber - 1];
    if (feederRound) feederRound.loser_next_round_id = fedGroup.rounds[0].id;
  };

  const eGroup = build('E', teams.length, teams);

  const wGroup = build('W', realLosersInRound(eGroup, COMPASS_FEEDER_MAP.W.round));
  const nGroup = build('N', realLosersInRound(eGroup, COMPASS_FEEDER_MAP.N.round));
  const sGroup = build('S', realLosersInRound(eGroup, COMPASS_FEEDER_MAP.S.round));
  link('E', COMPASS_FEEDER_MAP.W.round, wGroup);
  link('E', COMPASS_FEEDER_MAP.N.round, nGroup);
  link('E', COMPASS_FEEDER_MAP.S.round, sGroup);

  const nwGroup = build('NW', realLosersInRound(wGroup, COMPASS_FEEDER_MAP.NW.round));
  const swGroup = build('SW', realLosersInRound(wGroup, COMPASS_FEEDER_MAP.SW.round));
  const neGroup = build('NE', realLosersInRound(nGroup, COMPASS_FEEDER_MAP.NE.round));
  const seGroup = build('SE', realLosersInRound(sGroup, COMPASS_FEEDER_MAP.SE.round));
  link('W', COMPASS_FEEDER_MAP.NW.round, nwGroup);
  link('W', COMPASS_FEEDER_MAP.SW.round, swGroup);
  link('N', COMPASS_FEEDER_MAP.NE.round, neGroup);
  link('S', COMPASS_FEEDER_MAP.SE.round, seGroup);

  return {
    format: 'compass_draw',
    formatName: 'Compass Draw',
    activeGroups: COMPASS_GROUPS.filter(g => groups[g]),
    groups,
    rounds: allRounds,
    matches: allMatches,
    participants: teams,
    totalMatches: allMatches.length,
  };
};

/**
 * Format description card for the "Choose Tournament Format" UI — merged
 * into TOURNAMENT_FORMATS at the import site in ScheduleTab.jsx (wiring
 * tracked in a follow-up issue). `sports` is new — no other format descriptor
 * gates itself to specific sports today; a follow-up issue filters the
 * format-selector dropdown by it.
 */
export const COMPASS_DRAW_FORMAT = {
  id: 'compass_draw',
  name: 'Compass Draw',
  shortName: 'Compass',
  description: 'A single-elimination main draw (East) that cascades into up to 7 consolation brackets — West, North, South, Northwest, Southwest, Northeast, Southeast — seeded by the losers of specific rounds, so nearly everyone plays more than one match.',
  minTeams: 8,
  icon: 'compass',
  recommendedFor: 'Individual/doubles draws (larger sizes — 16, 32+ — populate more of the 8 directions)',
  sports: ['pickleball', 'tennis'],
};

export default generateCompassDraw;

// ── CLI ──────────────────────────────────────────────────────────────────
// Run directly with: node src/tournaments/utils/compassDrawScheduler.js --teams=16
//
// This file is also imported by ScheduleTab.jsx and bundled into the browser
// build, where `process` doesn't exist (or isn't the real Node process) —
// guard on that first so this block only ever runs under Node, never in-app.
// Not part of the app and unreachable from it, so it is not counted: the
// guard above is false under both the browser build and the test runner.
/* v8 ignore start */
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const args = Object.fromEntries(
    process.argv.slice(2).map(arg => {
      const [key, value] = arg.replace(/^--/, '').split('=');
      return [key, value];
    })
  );

  const teamCount = Number(args.teams) || 16;
  const participants = Array.from({ length: teamCount }, (_, i) => ({ id: `team-${i + 1}`, name: `Team ${i + 1}` }));

  const result = generateCompassDraw(participants);
  console.log(`Input: ${teamCount} teams`);
  console.log('Active groups:', result.activeGroups.join(', '));
  Object.values(result.groups).forEach(g => {
    console.log(`  ${g.groupName}: entrants=${g.entrantCount} bracketSize=${g.bracketSize} byes=${g.byesNeeded} rounds=${g.totalRounds}`);
  });
  console.log('Total matches:', result.totalMatches);
}
/* v8 ignore stop */
